import React, { useState, ChangeEvent, FormEvent, useEffect } from "react";
import { connect } from "react-redux";
import { authLogin } from "../../store/action";
import {
  LoginErrorProps,
  LoginProps,
  stateStatusType,
} from "../../store/actionType";
import { useNavigate } from "react-router";
import { loginFormValidator } from "../../../../../utils/helpers/loginFormValidator";
import { CustomButton, Error, InputForm } from "components";
import { EyeIcon, EyeSlashIcon, KeyIcon, ProfileIcon } from "assets";
import { useUser } from "utils/helpers/UserContext";
import { flattenRoleActions, resolveSuperAdmin, roleNames } from "utils/helpers/userAuthorities";

type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type StateProps = ReturnType<typeof mapStateToProps>;
type Props = DispatchProps & StateProps;

const LoginForm: React.FC<Props> = ({ onAuthLogin, loginState }) => {
  const [formData, setFormData] = useState<LoginProps>({
    login_id: "",
    password: "",
  });
  const [errors, setErrors] = useState<Partial<LoginErrorProps>>({});
  const [redirect, setRedirect] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);

  const navigate = useNavigate();
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const formErrors = loginFormValidator(formData);
    setErrors(formErrors);
    if (Object.keys(formErrors).length === 0) {
      try {
        setLoading(true);
        const loginInput: LoginProps = {
          login_id: formData.login_id,
          password: formData.password,
        };
        onAuthLogin(loginInput);
        setRedirect(true);
      } catch (error) {
        setErrors({ general_error: "Something went wrong" });
      }
    }
  };

  useEffect(() => {
    if (
      loginState.status === stateStatusType.SUCCESS &&
      loginState.authUser.token !== null
    ) {
      navigate("/main-dashboard");
      setLoading(false);
    } else if (loginState.status === stateStatusType.FAILED && redirect) {
      setErrors({ general_error: loginState.error });
      setLoading(false);
    }
  }, [loginState, redirect]);
const { setAuthUserProp, setAction, setSuperAdmin } = useUser();

useEffect(() => {
  if (
    loginState.status === stateStatusType.SUCCESS &&
    loginState.authUser.token !== null
  ) {
    const user = loginState.authUser.user;

    setAuthUserProp(loginState.authUser);
    setAction(flattenRoleActions(user.roles));
    setSuperAdmin(resolveSuperAdmin(roleNames(user.roles)));

    navigate("/main-dashboard");
    setLoading(false);
  }
}, [loginState, navigate, setAuthUserProp, setAction, setSuperAdmin]);
  return (
    <>
      <form>
        <div className="relative w-full mb-3">
          <InputForm
            styleLabel="text-xs font-semibold after:content-['*'] after:ml-1 after:text-red-500"
            label="Username"
            type="text"
            id="login_id"
            name="login_id"
            leadingIcon={<ProfileIcon className="size-5" />}
            value={formData.login_id}
            onChange={handleChange}
          />
          {errors.login_id && <Error>{errors.login_id}</Error>}
        </div>
        <div className="relative w-full mb-3 ">
          <InputForm
            styleLabel="text-xs font-semibold after:content-['*'] after:ml-1 after:text-red-500"
            label="Password"
            id="password"
            name="password"
            onChange={handleChange}
            value={formData.password}
            autoComplete="off"
            type={showPassword ? "text" : "password"}
            leadingIcon={<KeyIcon className="size-5" />}
            endingIcon={
              <div
                className="flex items-center"
                onClick={() => setShowPassword((prev) => !prev)}
                style={{ width: "20px", height: "20px" }}
              >
                {showPassword ? <EyeIcon /> : <EyeSlashIcon />}
              </div>
            }
          />
          {errors.password && <Error>{errors.password}</Error>}
        </div>
        <div className="text-center mt-6">
          <CustomButton
            onClick={handleSubmit}
            type="submit"
            loading={loading}
            loadingText="Signing in..."
            className="bg-primary-500 text-white active:bg-blueGray-600 text-sm font-bold uppercase px-6 py-3 rounded shadow hover:shadow-lg hover:bg-primary-600 outline-none focus:outline-none mr-1 mb-1 w-full ease-linear transition-all duration-150"
          >
            SIGN IN
          </CustomButton>
        </div>
      </form>
    </>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  onAuthLogin: (input: LoginProps) => dispatch(authLogin(input)),
});

const mapStateToProps = (state: any) => ({
  loginState: state.auth.authState,
});

export default connect(mapStateToProps, mapDispatchToProps)(LoginForm);
